import { Button, Flex, Heading, Tooltip } from "@radix-ui/themes";
import { FiEdit2 } from "react-icons/fi";
import ShareIcon from "@/assets/icons/share_filled.svg?react";
import LocationBadge from "@/components/creators/LocationBadge";
import FollowButton from "@/components/ui/FollowButton";

type CreatorHeaderProps = {
  stageName: string;
  creatorId?: string;
  canEdit: boolean;
  onShare: () => void;
  onEdit: () => void;
  onDoneEditing?: () => void;
  isEditing?: boolean;
  location?: { city?: string | null; country?: string | null } | null;
  primaryInk: string;
  primary: string;
  onPrimary: string;
  wrapperClassName?: string;
  /**
   * Suppress Follow when the viewer is the owner previewing their own public
   * profile — `canEdit` is already false there, so it would otherwise render.
   */
  hideFollow?: boolean;
};

export function CreatorHeader({
  stageName,
  creatorId,
  canEdit,
  onShare,
  onEdit,
  onDoneEditing,
  isEditing = false,
  location,
  primaryInk,
  primary,
  onPrimary,
  wrapperClassName,
  hideFollow = false,
}: CreatorHeaderProps) {
  return (
    <div className={wrapperClassName}>
      {isEditing ? (
        <Flex align="center" justify="between" className="my-8 md:my-4">
          <Heading className="text-[24px] md:text-[48px] text-blue-900 font-bold leading-[110%] tracking-[-0.48px]">
            Edit Profile
          </Heading>
          {onDoneEditing && (
            <button
              type="button"
              onClick={onDoneEditing}
              className="flex-shrink-0 flex items-center justify-center text-sm font-semibold text-blue-900 border border-blue-900 rounded hover:opacity-80 transition-opacity h-8 px-2 md:h-9 md:px-3"
            >
              View Public Profile
            </button>
          )}
        </Flex>
      ) : (
        <>
          <Flex className="items-center justify-between gap-4 my-4">
            <Heading
              className="overflow-hidden text-ellipsis text-[36px] lg:text-[48px] font-bold leading-[110%] tracking-[-0.48px] whitespace-nowrap"
              style={{ color: primaryInk }}
              data-testid="creator-profile-name"
            >
              {stageName || "Unknown Artist"}
            </Heading>
            {canEdit && (
              <Button
                variant="solid"
                onClick={onEdit}
                className="flex items-center gap-2 flex-shrink-0 text-sm font-semibold leading-[140%] px-4 h-[34px] rounded-full transition-all hover:brightness-90"
                style={{ backgroundColor: primary, color: onPrimary }}
              >
                <FiEdit2 className="w-3 h-3" />
                <span className="lg:hidden">Edit</span>
                <span className="hidden lg:inline">Edit Profile</span>
              </Button>
            )}
          </Flex>
          {location?.city && (
            <LocationBadge
              city={location.city}
              country={location.country}
              color={primaryInk}
            />
          )}
          {/* Follow / Share action row */}
          <Flex className="items-center gap-3 mt-3">
            {!canEdit && !hideFollow && creatorId && (
              <FollowButton
                creatorId={creatorId}
                primary={primary}
                onPrimary={onPrimary}
                primaryInk={primaryInk}
              />
            )}
            {/* Share entry, icon only (LUT-982) */}
            <Tooltip content="Share">
              <Button
                variant="outline"
                onClick={onShare}
                aria-label="Share creator profile"
                className="flex items-center justify-center flex-shrink-0 w-[34px] px-0 h-[34px] rounded-full transition-all hover:brightness-90 cursor-pointer"
                style={{
                  color: primaryInk || "#525966",
                  boxShadow: `inset 0 0 0 1px ${primaryInk || "#525966"}`,
                }}
              >
                <ShareIcon width={16} height={16} />
              </Button>
            </Tooltip>
          </Flex>
        </>
      )}
    </div>
  );
}
